package com.yendo.mx.repository

import com.yendo.mx.models.Favorito
import com.yendo.mx.utils.getAhora
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.*

object FavoritosRepository {
    private val auth = Firebase.auth
    private val db = Firebase.firestore
    private val scope = CoroutineScope(Dispatchers.Main + SupervisorJob())

    private val _favoritos = MutableStateFlow<List<Favorito>>(emptyList())
    val favoritos: StateFlow<List<Favorito>> = _favoritos.asStateFlow()

    // 🚀 INDEX O(1) EN MEMORIA PARA RENDERIZADO INSTANTÁNEO EN CARDS Y CARROUSELES
    private val _favoritosSet = MutableStateFlow<Set<String>>(emptySet())
    val favoritosSet: StateFlow<Set<String>> = _favoritosSet.asStateFlow()

    private var jobObservacion: Job? = null

    init {
        // Observar cambios en el estado de autenticación para iniciar/detener observación
        scope.launch {
            auth.authStateChanged.collect { user ->
                if (user != null) {
                    iniciarObservacion(user.uid)
                } else {
                    detenerObservacion()
                }
            }
        }
    }

    private fun iniciarObservacion(uid: String) {
        jobObservacion?.cancel()
        jobObservacion = scope.launch {
            try {
                db.collection("Usuarios").document(uid).collection("Favoritos")
                    .snapshots.catch { e ->
                        println("YENDO_LOG ⚠️ [FAV] Error en flujo: ${e.message}")
                    }.collect { snapshot ->
                        val lista = snapshot.documents.mapNotNull { doc ->
                            try {
                                doc.data<Favorito>().copy(id = doc.id)
                            } catch (e: Exception) {
                                println("YENDO_LOG ❌ [FAV] Error mapeando: ${e.message}")
                                null
                            }
                        }
                        _favoritos.value = lista
                        _favoritosSet.value = lista.map { it.id }.toSet()
                    }
            } catch (e: Exception) {
                println("YENDO_LOG ⚠️ [FAV] Error observando: ${e.message}")
            }
        }
    }

    private fun detenerObservacion() {
        jobObservacion?.cancel()
        _favoritos.value = emptyList()
    }

    suspend fun alternarFavorito(id: String, tipo: String, idRestauranteAsociado: String = ""): Boolean {
        val uid = auth.currentUser?.uid ?: return false
        val docRef = db.collection("Usuarios").document(uid).collection("Favoritos").document(id)

        return try {
            val doc = docRef.get()
            if (doc.exists) {
                docRef.delete()
                println("YENDO_LOG 💔 [FAV] Eliminado: $id ($tipo)")
                false // Ya no es favorito
            } else {
                val nuevoFav = Favorito(
                    id = id,
                    tipo = tipo,
                    fechaAgregado = getAhora(),
                    idRestauranteAsociado = idRestauranteAsociado
                )
                docRef.set(nuevoFav)
                println("YENDO_LOG ❤️ [FAV] Agregado: $id ($tipo)")
                true // Ahora es favorito
            }
        } catch (e: Exception) {
            println("YENDO_LOG ❌ [FAV] Error al alternar: ${e.message}")
            false
        }
    }

    fun esFavorito(id: String): Boolean {
        return _favoritos.value.any { it.id == id }
    }
}
